import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE, authHeaders } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

const fetchDocBlob = (doc) =>
  axios.get(`${API_BASE}/api/documents/${doc.id}/download`, { headers: authHeaders(), responseType: 'blob' })
    .then(r => r.data);

export async function downloadDocument(doc) {
  try {
    const url = window.URL.createObjectURL(await fetchDocBlob(doc));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', doc.original_filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch { toast.error('Download failed'); }
}

const kindOf = (type, name) => {
  if (type.includes('pdf') || /\.pdf$/i.test(name)) return 'pdf';
  if (type.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp)$/i.test(name)) return 'image';
  if (type.includes('wordprocessingml') || /\.docx$/i.test(name)) return 'docx';
  return 'other';
};

/** Shows a PDF, image or Word (.docx) document in place; anything else
 * (e.g. an old .doc file) gets a download button instead. */
export default function DocumentPreview({ doc, onClose }) {
  // Keep the last document while the dialog animates closed, after `doc` is already null.
  const [shown, setShown] = useState(doc);
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const docxRef = useRef(null);

  useEffect(() => { if (doc) setShown(doc); }, [doc]);

  useEffect(() => {
    if (!doc) return;
    let objectUrl = null;
    let cancelled = false;
    setBlob(null); setUrl(null); setFailed(false);
    fetchDocBlob(doc)
      .then(b => {
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(b);
        setBlob(b);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objectUrl) window.URL.revokeObjectURL(objectUrl); };
  }, [doc]);

  const name = shown?.original_filename || '';
  const kind = kindOf(blob?.type || shown?.content_type || '', name);

  // Word files: docx-preview turns the .docx into HTML. Loaded only when needed.
  useEffect(() => {
    if (kind !== 'docx' || !blob || !docxRef.current) return;
    let cancelled = false;
    const el = docxRef.current;
    import('docx-preview')
      .then(({ renderAsync }) => !cancelled && renderAsync(blob, el, undefined, { inWrapper: true, ignoreLastRenderedPageBreak: true }))
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; el.innerHTML = ''; };
  }, [kind, blob]);

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="document-preview">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">{name}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 rounded-lg border border-contrast/10 bg-sunken flex items-center justify-center overflow-auto">
          {failed ? (
            <div className="text-center space-y-3 p-6">
              <p className="text-sm text-ink-2">Could not show this document here. Download it to open.</p>
              {shown && (
                <Button onClick={() => downloadDocument(shown)} className="gap-2 bg-brand hover:bg-brand-soft text-white">
                  <Download className="w-4 h-4" /> Download
                </Button>
              )}
            </div>
          ) : !url ? (
            <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
          ) : kind === 'pdf' ? (
            <iframe title="Document preview" src={url} className="w-full h-full rounded-lg bg-white" />
          ) : kind === 'image' ? (
            <img src={url} alt={name} className="max-w-full max-h-full object-contain" />
          ) : kind === 'docx' ? (
            <div ref={docxRef} className="w-full h-full overflow-auto rounded-lg bg-white text-black" data-testid="docx-preview" />
          ) : (
            <div className="text-center space-y-3 p-6">
              <p className="text-sm text-ink-2">
                {/\.doc$/i.test(name)
                  ? 'Older Word (.doc) files can\'t be shown in the browser. Download it to open, or save it as .docx and upload again.'
                  : 'Preview isn\'t available for this file type. Download it to open.'}
              </p>
              {shown && (
                <Button onClick={() => downloadDocument(shown)} className="gap-2 bg-brand hover:bg-brand-soft text-white">
                  <Download className="w-4 h-4" /> Download
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
